// Iter 82.1 / Phase E1.1 — Year-End Leave Carry-Forward console.
// Preview → Review → Commit flow (Primary-Admin only for commit).
// Computes per-(employee, leave-type) projection rows: existing balance,
// eligibility, limit, expiry days, final carry-forward. Commit creates
// an immutable snapshot record for forensic audit.
import { useCallback, useEffect, useState } from "react";
import { Eye, CheckCircle, Warning, ShieldCheck, DownloadSimple, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

export default function LeaveCarryForward() {
    const { user } = useAuth();
    const isPrimary = !!user?.is_primary_admin;
    const [fys, setFys] = useState([]);
    const [fromFy, setFromFy] = useState("");
    const [toFy, setToFy] = useState("");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snapshots, setSnapshots] = useState([]);
    const [committing, setCommitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAck, setConfirmAck] = useState(false);

    const loadMeta = useCallback(async () => {
        try {
            const [fyResp, sResp] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/leave/carry-forward/snapshot/list"),
            ]);
            const items = fyResp.data.items || [];
            setFys(items);
            setSnapshots(sResp.data.items || []);
            const current = items.find((f) => f.is_current);
            if (current && !fromFy) setFromFy(current.code);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load metadata"));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);

    const runPreview = async () => {
        if (!fromFy || !toFy) {
            toast.error("Select both From FY and To FY");
            return;
        }
        if (fromFy === toFy) {
            toast.error("From FY and To FY must differ");
            return;
        }
        setLoading(true);
        setPreview(null);
        try {
            const { data } = await api.post("/leave/carry-forward/preview", {
                from_fiscal_year: fromFy,
                to_fiscal_year: toFy,
                employee_ids: [],
                leave_type_codes: [],
            });
            setPreview(data);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const exportSnapshots = async () => {
        try {
            const { data: blob } = await api.get("/leave/carry-forward/snapshot/export", { responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-carry-forward-snapshots-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "CSV export failed"));
        }
    };

    const commit = async () => {
        if (!confirmAck) {
            toast.error("Please acknowledge the impact summary first");
            return;
        }
        setCommitting(true);
        try {
            const { data } = await api.post("/leave/carry-forward/commit", {
                from_fiscal_year: fromFy,
                to_fiscal_year: toFy,
                employee_ids: [],
                leave_type_codes: [],
            });
            toast.success(`Carry-forward committed · ${data.posted} posted, ${data.skipped_idempotent} idempotent skips`);
            setConfirmOpen(false);
            setConfirmAck(false);
            setPreview(null);
            loadMeta();
        } catch (err) {
            toast.error(formatApiError(err, "Commit failed"));
        } finally {
            setCommitting(false);
        }
    };

    const totals = preview?.totals || { rows: 0, existing_balance: 0, final_carry_forward: 0, expiry_amount: 0 };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="leave-carry-forward-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Year-End Carry-Forward"
                description="Preview → Review → Commit. Compute carry-forward and expiry per policy at year-end transition. Commit is restricted to the Primary Admin and creates an immutable audit snapshot."
                actions={(
                    <Button variant="outline" size="sm" onClick={exportSnapshots} data-testid="cf-export-snapshots-btn">
                        <DownloadSimple size={14} className="mr-1" /> Export Snapshot Log
                    </Button>
                )}
            />

            {!isPrimary && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="cf-primary-banner">
                    <ShieldCheck size={16} className="text-amber-700 mt-0.5" />
                    <span>You can preview and generate reports here, but the final commit action is restricted to the Primary Admin per governance policy.</span>
                </div>
            )}

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="cf-selector">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Year-End Transition</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                    <div>
                        <label className="text-xs text-slate-600">From Fiscal Year (closing)</label>
                        <select
                            data-testid="cf-from-fy"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={fromFy}
                            onChange={(e) => { setFromFy(e.target.value); setPreview(null); }}
                        >
                            <option value="">Select…</option>
                            {fys.map((fy) => (<option key={fy.id} value={fy.code}>{fy.code}{fy.is_current ? " (current)" : ""}</option>))}
                        </select>
                    </div>
                    <div className="text-center text-slate-400 hidden md:block"><ArrowRight size={20} /></div>
                    <div>
                        <label className="text-xs text-slate-600">To Fiscal Year (opening)</label>
                        <select
                            data-testid="cf-to-fy"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={toFy}
                            onChange={(e) => { setToFy(e.target.value); setPreview(null); }}
                        >
                            <option value="">Select…</option>
                            {fys.map((fy) => (<option key={fy.id} value={fy.code}>{fy.code}</option>))}
                        </select>
                    </div>
                    <div className="md:col-span-2 flex gap-2">
                        <Button onClick={runPreview} disabled={loading || !fromFy || !toFy || fromFy === toFy} data-testid="cf-preview-btn">
                            <Eye size={14} className="mr-1" /> {loading ? "Computing…" : "Generate Preview"}
                        </Button>
                        {isPrimary && preview && (
                            <Button variant="default" className="bg-rose-600 hover:bg-rose-700" onClick={() => setConfirmOpen(true)} data-testid="cf-commit-btn">
                                Commit Year-End Close
                            </Button>
                        )}
                    </div>
                </div>
            </section>

            {preview && (
                <>
                    <section className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cf-totals">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                            <div className="text-xs text-slate-700 uppercase tracking-wider">Rows</div>
                            <div className="text-2xl font-bold text-slate-900 mt-1">{totals.rows}</div>
                        </div>
                        <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-center">
                            <div className="text-xs text-sky-700 uppercase tracking-wider">Existing Balance</div>
                            <div className="text-2xl font-bold text-sky-900 mt-1">{Number(totals.existing_balance).toFixed(2)}</div>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                            <div className="text-xs text-indigo-700 uppercase tracking-wider">Carry Forward</div>
                            <div className="text-2xl font-bold text-indigo-900 mt-1">{Number(totals.final_carry_forward).toFixed(2)}</div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                            <div className="text-xs text-orange-700 uppercase tracking-wider">Will Expire</div>
                            <div className="text-2xl font-bold text-orange-900 mt-1">{Number(totals.expiry_amount).toFixed(2)}</div>
                        </div>
                    </section>

                    <section className="mt-4 bg-white rounded-xl border border-slate-200" data-testid="cf-preview-table">
                        <div className="px-4 py-3 border-b border-slate-100">
                            <h3 className="text-sm font-semibold text-slate-800">Per-Employee Carry-Forward Projection</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Preview · No ledger entries written until Commit</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600 text-xs">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Employee Code</th>
                                        <th className="text-left px-3 py-2 font-medium">Employee Name</th>
                                        <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                        <th className="text-left px-3 py-2 font-medium">Policy</th>
                                        <th className="text-right px-3 py-2 font-medium">Current Balance</th>
                                        <th className="text-center px-3 py-2 font-medium">CF Eligible</th>
                                        <th className="text-right px-3 py-2 font-medium">CF Limit</th>
                                        <th className="text-right px-3 py-2 font-medium">Expiring</th>
                                        <th className="text-right px-3 py-2 font-medium">Final CF</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.length === 0 && (
                                        <tr><td colSpan={9} className="text-center py-10 text-slate-400">No balances to carry forward for the selected fiscal years.</td></tr>
                                    )}
                                    {preview.rows.map((r, i) => (
                                        <tr key={`${r.employee_id}-${r.leave_type_code}`} className="border-t border-slate-100" data-testid={`cf-prev-row-${i}`}>
                                            <td className="px-3 py-2 font-mono text-xs">{r.employee_code}</td>
                                            <td className="px-3 py-2">{r.employee_name}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                            <td className="px-3 py-2 text-xs text-slate-600">{r.policy_name || "—"}</td>
                                            <td className="px-3 py-2 text-right font-semibold">{Number(r.existing_balance).toFixed(2)}</td>
                                            <td className="px-3 py-2 text-center">
                                                {r.eligible_for_carry_forward ? (
                                                    <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge>
                                                ) : (
                                                    <Badge className="bg-slate-100 text-slate-700">No</Badge>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-slate-600 text-xs">{r.carry_forward_limit > 0 ? Number(r.carry_forward_limit).toFixed(2) : "—"}</td>
                                            <td className="px-3 py-2 text-right text-orange-700">{Number(r.expiry_amount).toFixed(2)}</td>
                                            <td className="px-3 py-2 text-right font-semibold text-indigo-700">{Number(r.final_carry_forward).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}

            <section className="mt-6 bg-white rounded-xl border border-slate-200" data-testid="cf-snapshot-section">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800">Historical Snapshots</h3>
                    <span className="text-xs text-slate-500">{snapshots.length} commit{snapshots.length === 1 ? "" : "s"}</span>
                </div>
                {snapshots.length === 0 ? (
                    <div className="text-center text-sm text-slate-400 py-10">No year-end carry-forward has been committed yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Snapshot ID</th>
                                    <th className="text-left px-3 py-2 font-medium">From FY</th>
                                    <th className="text-left px-3 py-2 font-medium">To FY</th>
                                    <th className="text-right px-3 py-2 font-medium">Employees</th>
                                    <th className="text-right px-3 py-2 font-medium">Leave Types</th>
                                    <th className="text-right px-3 py-2 font-medium">Posted</th>
                                    <th className="text-right px-3 py-2 font-medium">Carry Forward Total</th>
                                    <th className="text-right px-3 py-2 font-medium">Expired Total</th>
                                    <th className="text-left px-3 py-2 font-medium">Executed By</th>
                                    <th className="text-left px-3 py-2 font-medium">Executed At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {snapshots.map((s) => (
                                    <tr key={s.id} className="border-t border-slate-100" data-testid={`cf-snap-row-${s.id}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{s.id}</td>
                                        <td className="px-3 py-2 font-mono">{s.from_fiscal_year}</td>
                                        <td className="px-3 py-2 font-mono">{s.to_fiscal_year}</td>
                                        <td className="px-3 py-2 text-right">{s.total_employees_processed}</td>
                                        <td className="px-3 py-2 text-right">{s.total_leave_types_processed}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{s.posted_count}</td>
                                        <td className="px-3 py-2 text-right text-indigo-700">{Number(s.totals?.final_carry_forward || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-orange-700">{Number(s.totals?.expiry_amount || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-xs">{s.executed_by_name}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(s.executed_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setConfirmAck(false); }}>
                <DialogContent data-testid="cf-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Commit Year-End Carry-Forward</DialogTitle>
                        <DialogDescription>Irreversible year-end close — review the impact summary and acknowledge before committing.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-900 flex items-start gap-2">
                            <Warning size={18} className="text-rose-700 mt-0.5" />
                            <div>
                                <strong>This action is irreversible.</strong> Carry-forward and expiry entries will be written to the leave ledger for every eligible employee. A permanent snapshot record will be created for audit.
                            </div>
                        </div>
                        {preview && (
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
                                <div>From → To: <span className="font-mono font-semibold">{fromFy} → {toFy}</span></div>
                                <div>Rows: <span className="font-semibold">{totals.rows}</span></div>
                                <div>Total Existing Balance: <span className="font-semibold">{Number(totals.existing_balance).toFixed(2)}</span> days</div>
                                <div>Total Carry-Forward: <span className="font-semibold text-indigo-700">{Number(totals.final_carry_forward).toFixed(2)}</span> days</div>
                                <div>Total Expiring: <span className="font-semibold text-orange-700">{Number(totals.expiry_amount).toFixed(2)}</span> days</div>
                            </div>
                        )}
                        <label className="flex items-start gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox" data-testid="cf-confirm-ack"
                                checked={confirmAck}
                                onChange={(e) => setConfirmAck(e.target.checked)}
                                className="mt-1"
                            />
                            <span>I have reviewed the impact summary above and authorise the year-end carry-forward commit.</span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setConfirmOpen(false); setConfirmAck(false); }} data-testid="cf-confirm-cancel">Cancel</Button>
                        <Button className="bg-rose-600 hover:bg-rose-700" onClick={commit} disabled={committing || !confirmAck} data-testid="cf-confirm-submit">
                            <CheckCircle size={14} className="mr-1" /> {committing ? "Committing…" : "Commit Year-End Close"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
